import { useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery } from 'react-query';
import toast from 'react-hot-toast';
import { Form } from 'semantic-ui-react';
import { Button, Modal } from '~/components';
import { FormSchemaAwareWidget } from '~/components/form';
import Link from '~/components/Link';
import { useForm, useUserContext } from '~/hooks';
import projectDefaultValues from '~/schema/project/projectDefaultValues';
import { STATES } from '~/utils';
import { Project } from '~/types/project';
import { ModalFormProps } from './types';
import { useProjectList } from '~/requests/projects/useProjectList';
import { useCreateProject } from '~/mutations/project/useCreateProject';
import { getInstallers } from '~/api/admin';

interface CreateProjectFormState {
  name: string;
  addressState: string;
}

export default function CreateProjectForm({ 
  toggleFormType, 
  portfolioId,
  closeModal,
  projectData = {},
  installerId
}: ModalFormProps) {
  const { data: projects } = useProjectList();
  const history = useHistory();
  const { formState } = useForm<CreateProjectFormState>();
  const { loggedInUser } = useUserContext();

  const [hasPv, setHasPv] = useState(false);
  const [pvSizeMwdc, setPvSizeMwdc] = useState('');
  const [hasBess, setHasBess] = useState(false);
  const [bessWattage, setBessWattage] = useState('');
  const [bessCapacity, setBessCapacity] = useState('');

  const isAdmin = Boolean(loggedInUser?.isAdmin);
  // Admins must create projects on behalf of a specific installer. Fall back to
  // projectData.installerId (set by upstream flows like the CreateDealModal).
  const resolvedInstallerId = installerId || (projectData as { installerId?: string })?.installerId;
  const isInstallerMissingForAdmin = isAdmin && !resolvedInstallerId;

  // Fetch installers so admins can see which installer this project will be
  // created for.
  const { data: installers = [] } = useQuery({
    queryKey: ['admin', 'installers'],
    queryFn: () => getInstallers(),
    enabled: isAdmin && Boolean(resolvedInstallerId),
  });
  const selectedInstallerName = useMemo(() => {
    if (!resolvedInstallerId) return null;
    const match = (installers as { id: string; company?: { name?: string } }[])
      .find((inst) => inst.id === resolvedInstallerId);
    return match?.company?.name ?? null;
  }, [installers, resolvedInstallerId]);

  // Determine if form has an error
  const pvIncomplete = hasPv && !(Number(pvSizeMwdc) > 0);
  const bessIncomplete =
    hasBess && (!(Number(bessWattage) > 0) || !(Number(bessCapacity) > 0));
  const hasError =
    !formState.name ||
    !formState.addressState ||
    isInstallerMissingForAdmin ||
    pvIncomplete ||
    bessIncomplete;

  const { mutateAsync: createProject, status: createProjectStatus } = useCreateProject();

  const handleSubmit = async () => {
    if (isInstallerMissingForAdmin) {
      toast.error('Please select an installer before creating a project.');
      return;
    }
    try {
      const project = await createProject({
        ...projectDefaultValues,
        ...projectData,
        name: formState?.name,
        addressState: formState?.addressState,
        portfolioId,
        ...(resolvedInstallerId && { installerId: resolvedInstallerId }),
        ...(hasPv && {
          projectArrays: [
            { order: 0, sizeKwdc: Number(pvSizeMwdc) * 1000 },
          ] as Project['projectArrays'],
        }),
        ...(hasBess && {
          hasStorage: true,
          storage: [
            {
              order: 0,
              wattage: Number(bessWattage) * 1000,
              discharge: Number(bessCapacity) * 1000,
            },
          ] as Project['storage'],
        }),
      });
      toast.success(`Project created successfully ${portfolioId ? ' and added to the portfolio.' : '.'}`);
      if (project?.id) {
        history.push(`/project/${project?.id}/worksheet`);
        closeModal();
      }
    } catch (err) {
      console.error('Error occurred creating project:', err);
      toast.error('An error occurred while creating the project. Please try again.');
    }
  };

  return (
    <div>
      <Modal.Title>
        Create a new project
      </Modal.Title>
      <Modal.Content>
        <p>
          Start with the basics so we can customize your project information based on location.
        </p>
        {isAdmin && resolvedInstallerId && (
          <div
            style={{
              padding: 'var(--small) var(--medium)',
              marginBottom: 'var(--medium)',
              border: '1px solid var(--color-neutral-200, #e0e0e0)',
              borderRadius: 4,
              background: 'var(--color-neutral-50, #fafafa)',
              fontSize: '0.95rem',
            }}
          >
            <strong>Installer:</strong>{' '}
            {selectedInstallerName ?? 'Loading installer…'}
          </div>
        )}
        <Form onSubmit={() => handleSubmit()}>
          <FormSchemaAwareWidget
            fieldLabel="Project name"
            placeholder="Enter value"
            schemaKey="name"
          />
          <FormSchemaAwareWidget
            fieldLabel="State"
            placeholder="Enter value"
            schemaKey="addressState"
            search
            options={STATES}
          />
          <Form.Checkbox
            label="This project includes solar (PV)"
            checked={hasPv}
            onChange={(_, { checked }) => setHasPv(Boolean(checked))}
          />
          {hasPv && (
            <Form.Input
              type="number"
              label="System size (MWdc)"
              placeholder="Enter size"
              value={pvSizeMwdc}
              onChange={(e) => setPvSizeMwdc(e.target.value)}
            />
          )}
          <Form.Checkbox
            label="This project includes storage (BESS)"
            checked={hasBess}
            onChange={(_, { checked }) => setHasBess(Boolean(checked))}
          />
          {hasBess && (
            <Form.Group widths="equal">
              <Form.Input
                type="number"
                label="Wattage (MW)"
                placeholder="Enter wattage"
                value={bessWattage}
                onChange={(e) => setBessWattage(e.target.value)}
              />
              <Form.Input
                type="number"
                label="Capacity (MWh)"
                placeholder="Enter capacity"
                value={bessCapacity}
                onChange={(e) => setBessCapacity(e.target.value)}
              />
            </Form.Group>
          )}
          {isInstallerMissingForAdmin && (
            <p style={{ color: '#b00020', marginTop: '0.5rem' }}>
              An installer must be selected before creating a project. Please go back and choose an installer.
            </p>
          )}
        </Form>
      </Modal.Content>
      <Modal.Footer>
        {!!projects?.length && (
          <Link 
            as={Button} 
            style={{ marginRight: 'auto' }}
            onClick={toggleFormType}
          >
            Duplicate a project
          </Link>
        )}
        <Button primary outlined onClick={closeModal}>Cancel</Button>
        <Button
          primary
          disabled={hasError || createProjectStatus === 'loading'}
          loading={createProjectStatus === 'loading'}
          onClick={() => handleSubmit()}
        >
          Create project
        </Button>
      </Modal.Footer>
    </div>
  );
}